import React, { useEffect, useState } from 'react';
import { Col, message, Row, Select, Table, Upload, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import { api, endpoints } from '../../../../api';
import ExpertiseListModalComponent from '../../../../components/ExpertiseListModalComponent';
import {
  CustomButton,
  CustomSelect,
  CustomTableContainer,
  DownloadCol,
  ExpertiseComponentContainer,
  FormContainer,
  FormInput,
  GridItem,
  ManualContainer,
} from '../../../../styles/styled';
import { getBase64 } from '../../../../utils/convertToBase64';
import { sortPublishers, sortStatus } from '../../../../utils/sortObject';
import { StatusesType } from '../../../ApplicationPage/types';
import { FileDataType, PublishersType, SchoolClassesType } from '../../../NewApplicationPage/types';
import { FileResponseType } from '../../../NewDocumentCategory/components/AddFIleForm/types';
import { ExpertiseExpertListType } from '../../../PovsExpertisePage/types';
import {
  ExpertFio,
  ExpertiseExpertsResponseType,
  ExpertiseExpertType,
  ExpertiseTextbookType,
  ExpertiseType,
} from '../../types';
import { PaginationType } from '../BooksExpertiseComponent/BooksExpertiseComponent';
import { ConclusionSearchType } from './types';

const { Option } = Select;

export const ExpertiseConclusionComponent = () => {
  const [statusesState, setStatuses] = useState<StatusesType>();
  const [publishersState, setPublishers] = useState<PublishersType>([]);
  const [schoolClassesState, setSchoolClasses] = useState<SchoolClassesType>([]);
  const [searchState, setSearchState] = useState<ConclusionSearchType>({});
  const [paginationState, setPagination] = useState<PaginationType>({ pagination: 1, perPage: 10, count: 0 });
  const [expertsState, setExpertsState] = useState<ExpertiseType[]>();
  const [searchLoadState, setSearchLoad] = useState<'clear' | 'neutral' | 'search'>('clear');
  const [currentOpenModalId, setCurrentOpenModal] = useState<number | undefined>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [chosenExpertiseList, setChosenExpertiseList] = useState<ExpertiseType | undefined>();
  const [currentElStatus, setCurrentElStatus] = useState<string | undefined>();
  const [currentExpertiseListId, setCurrentExpertiseListId] = useState<number | undefined>();
  const [fileLoad, setLoad] = useState(false);
  const [fileLoadState, setFileLoad] = useState<FileDataType | null>(null);
  const [loadData, setLoadData] = useState<boolean>(true);

  useEffect(() => {
    if (fileLoad && currentExpertiseListId)
      api.post<FileResponseType>(endpoints.addFile(), fileLoadState).then((el) =>
        api
          .put(endpoints.loadFinalList(currentExpertiseListId), {
            final_exp_file: { id: el.data.file_id },
          })
          .then(() => {
            message.success('Итоговый ЭЛ успешно загружен');

            setLoadData(true);
            setLoad(false);
          })
          .catch(() => message.error('Произошла ошибка при загрузке файла')),
      );

    // eslint-disable-next-line
  }, [fileLoadState]);

  useEffect(() => {
    (async () => {
      try {
        const statusesResponse = await api.get<StatusesType>(endpoints.getExpertiseListStatus());
        const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 1000));
        const schoolClassesResponse = await api.get<SchoolClassesType>(endpoints.getSchoolClasses());

        setStatuses(statusesResponse.data.sort(sortStatus));
        setPublishers(publishersResponse.data.sort(sortPublishers));
        setSchoolClasses(schoolClassesResponse.data);
      } catch (err) {}
    })();
  }, []);

  const handleEl: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'error') setLoad(true);

    getBase64(info.file.originFileObj as RcFile, (url) =>
      setFileLoad({
        base64: url?.split('base64,')[1],
        title: info.file.name as string,
        ext: info?.file?.name?.split('.')[1],
      }),
    );

    return;
  };

  useEffect(() => {
    (async () => {
      try {
        if (searchLoadState === 'clear') {
          const expertsResponse = await api.get<ExpertiseExpertsResponseType>(
            endpoints.getExpertises({ perPage: 10, page: paginationState.pagination - 1 }),
          );

          setExpertsState(
            expertsResponse.data.expertises.map((el, index) => {
              return { ...el, key: index };
            }),
          );

          setPagination((prevState) => {
            return { ...prevState, count: expertsResponse.data.count };
          });
        } else if (searchLoadState === 'search') {
          setSearchLoad('neutral');

          const params = {
            perPage: 999,
            page: paginationState.pagination - 1,
            ...searchState,
          };

          const expertsResponse = await api.get<ExpertiseExpertsResponseType>(endpoints.getExpertises(params));

          setPagination((prevState) => {
            return { ...prevState, pagination: 1, count: expertsResponse.data.expertises.length };
          });

          setExpertsState(
            expertsResponse.data.expertises.map((el, index) => {
              return { ...el, key: index };
            }),
          );
        }
      } catch (err) {}
    })();

    setLoadData(false);
    // eslint-disable-next-line
  }, [paginationState.pagination, searchLoadState, loadData]);

  const expertiseConclusionsTableColumns = [
    {
      title: '№',
      dataIndex: 'key',
      key: 'key',
      render: (key: number) => <p>{key + 1 + (paginationState.pagination - 1) * 10}</p>,
    },
    {
      title: 'ФИО эксперта',
      dataIndex: 'expert',
      key: 'expert',
      render: (value: ExpertFio) => <p>{value.last_name + ' ' + value.first_name + ' ' + value.patronymic}</p>,
    },
    {
      title: 'Наименование учебника',
      dataIndex: 'textbook',
      key: 'textbook',
      render: (value: ExpertiseTextbookType) => <p>{value.title}</p>,
    },
    {
      title: 'Класс',
      dataIndex: 'textbook',
      key: 'school_classes',
      render: (value: ExpertiseTextbookType) => <p>{value?.school_classes?.map((el) => el.title).join(', ')}</p>,
    },
    {
      title: 'Автор',
      dataIndex: 'textbook',
      key: 'author',
      render: (value: ExpertiseTextbookType) => <ManualContainer>{value?.author}</ManualContainer>,
    },
    {
      title: 'Наименование учебного пособия',
      dataIndex: 'textbook',
      key: 'manual_title',
      render: (value: ExpertiseTextbookType) => {
        return value?.text_book_edu_man?.map(
          (el, idx) => el.title && <ManualContainer key={`${value.key}-${idx}`}>{el.title}</ManualContainer>,
        );
      },
    },
    {
      title: 'Автор (авторский коллектив) учебного пособия',
      dataIndex: 'textbook',
      key: 'manual_author',
      render: (value: ExpertiseTextbookType) => {
        return value?.text_book_edu_man?.map(
          (el, idx) => el.fio && <ManualContainer key={`${value.key}-${idx}`}>{el.fio}</ManualContainer>,
        );
      },
    },
    {
      title: 'Издательство',
      dataIndex: 'textbook',
      key: 'publisher',
      render: (value: ExpertiseTextbookType) => <p>{value?.line_of_textbooks?.application?.publisher?.title}</p>,
    },
    {
      title: 'Дата',
      dataIndex: 'textbook',
      key: 'date',
      render: (value: ExpertiseTextbookType) => (
        <p style={{ minWidth: '70px' }}>
          {value?.line_of_textbooks?.application?.date?.split('-').reverse().join('.')}
        </p>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'expertise_list',
      key: 'status',
      render: (value: ExpertiseExpertType) => <p style={{ minWidth: '70px' }}>{value?.status?.name}</p>,
    },
    {
      title: 'Экспертный лист',
      dataIndex: 'expert_list',
      key: 'expert_list',
      render: (expertise_list: ExpertiseExpertListType, expertise: ExpertiseType) => {
        return (
          <GridItem gap="5px" justify="flex-start" align="center">
            <CustomButton
              onClick={() => {
                setCurrentOpenModal(expertise?.expertise_list?.id);
                setOpenModal(expertise_list?.status?.code !== 'draft');
                setCurrentElStatus(expertise?.expertise_list?.status?.code);
                setChosenExpertiseList(expertise);
              }}
              lined
              height="25px"
              padding="0 8px"
              width="120px"
              fontsize="10px !important"
              nowrap
            >
              Экспертный лист
            </CustomButton>
            {expertise?.expertise_list?.final_exp_file && (
              <a href={expertise.expertise_list.final_exp_file?.file_path} download target="_blank" rel="noreferrer">
                <CustomButton height="25px" padding="0 8px" width="120px" fontsize="10px !important" nowrap>
                  Итоговый эл
                </CustomButton>
              </a>
            )}
            {expertise?.expertise_list?.status?.code === 'complete' && (
              <button className="empty-button" onClick={() => setCurrentExpertiseListId(expertise?.expertise_list?.id)}>
                <DownloadCol noBorder span={24}>
                  <Upload onChange={handleEl} accept=".pdf, .png, .jpg, .jpeg" name="avatar" showUploadList={false}>
                    <div className="download-el">
                      {!expertise?.expertise_list?.final_exp_file ? 'Загрузить итоговый ЭЛ' : 'Заменить'}
                    </div>
                  </Upload>
                </DownloadCol>
              </button>
            )}
          </GridItem>
        );
      },
    },
  ];

  return (
    <ExpertiseComponentContainer>
      <ExpertiseListModalComponent
        id={currentOpenModalId}
        isPovs={currentElStatus !== 'sent'}
        expertise_list={chosenExpertiseList}
        open={openModal}
        isRejected={false}
        setOpen={(value) => setOpenModal(value)}
        setLoad={() => setLoadData(true)}
      />
      <FormContainer padding="10px" background={'#e9eef4'}>
        <Row gutter={[15, 15]}>
          <Col span={12}>
            <div className="text">ФИО эксперта</div>
            <FormInput
              value={searchState.fio}
              onChange={(e) => setSearchState({ ...searchState, fio: e.target.value })}
            />
          </Col>
          <Col span={12}>
            <div className="text">Наименование учебника</div>
            <FormInput
              value={searchState.name}
              onChange={(e) => setSearchState({ ...searchState, name: e.target.value })}
            />
          </Col>
          <Col span={12}>
            <div className="text">Класс</div>
            <CustomSelect
              onChange={(value, option) => {
                setSearchState({
                  ...searchState,
                  school_class: parseInt(Object.values(option)[0]),
                });
              }}
              value={
                searchState?.school_class
                  ? schoolClassesState.find((el) => parseInt(el.id) === searchState.school_class)?.title
                  : 'Все'
              }
              showSearch
            >
              <Option key={undefined} value={'Все'}>
                Все
              </Option>
              {schoolClassesState.map((el) => {
                return (
                  <Option key={el.id} value={el.title}>
                    {el.title}
                  </Option>
                );
              })}
            </CustomSelect>
          </Col>
          <Col span={12}>
            <div className="text">Автор (авторский коллектив)</div>
            <FormInput
              value={searchState.author}
              onChange={(e) => setSearchState({ ...searchState, author: e.target.value })}
            />
          </Col>
          <Col span={12}>
            <div className="text">Наименование издательства</div>
            <CustomSelect
              onChange={(value, option) => {
                setSearchState({
                  ...searchState,
                  publisher: parseInt(Object.values(option)[0]),
                });
              }}
              value={
                searchState?.publisher ? publishersState.find((el) => el.id === searchState.publisher)?.title : 'Все'
              }
              showSearch
            >
              <Option key={undefined} value={undefined}>
                Все
              </Option>
              {publishersState.map((el) => {
                return (
                  <Option key={el.id} value={el.title}>
                    {el.title}
                  </Option>
                );
              })}
            </CustomSelect>
          </Col>
          <Col span={12}>
            <div className="text">Статус экспертного листа</div>
            <CustomSelect
              onChange={(value, option) => {
                setSearchState({
                  ...searchState,
                  status: parseInt(Object.values(option)[0]),
                });
              }}
              value={searchState?.status ? statusesState?.find((el) => el.id === searchState.status)?.name : 'Все'}
              showSearch
            >
              {statusesState?.map((el) => {
                return (
                  <Option key={el.id} value={el.name}>
                    {el.name}
                  </Option>
                );
              })}
            </CustomSelect>
          </Col>
          <Col span={12}>
            <div className="text red-star">Год поступления материала</div>
            <CustomSelect
              value={searchState.year ? searchState.year : 'Все'}
              onChange={(value, option) => {
                setSearchState((prevState) => {
                  return { ...prevState, year: Object.values(option)[1] };
                });
              }}
              showSearch
            >
              <Option value={undefined} key={undefined}>
                Все
              </Option>
              <Option value={2020} key={2020}>
                2020
              </Option>
              <Option value={2021} key={2021}>
                2021
              </Option>
              <Option value={2022} key={2022}>
                2022
              </Option>
              <Option value={2023} key={2023}>
                2023
              </Option>
              <Option key={2024} value={2024}>
                2024
              </Option>
              <Option key={2025} value={2025}>
                2025
              </Option>
            </CustomSelect>
          </Col>
          <Col span={12} />
          <Col className="center right" span={12}>
            <CustomButton width="100%" onClick={() => setSearchLoad('search')}>
              Найти
            </CustomButton>
          </Col>
          <Col className="center right" span={12}>
            <CustomButton
              lined
              width="100%"
              onClick={() => {
                setSearchLoad('clear');
                setSearchState({});
              }}
            >
              Очистить
            </CustomButton>
          </Col>
        </Row>
      </FormContainer>
      <CustomTableContainer>
        <Table
          columns={expertiseConclusionsTableColumns}
          dataSource={expertsState || []}
          pagination={{
            current: paginationState.pagination,
            pageSize: paginationState.perPage,
            total: paginationState.count,
            onChange: (page, pageSize) => setPagination({ ...paginationState, perPage: pageSize, pagination: page }),
          }}
          className="table non-expand"
        />
      </CustomTableContainer>
    </ExpertiseComponentContainer>
  );
};
